/* Tapping a tab takes you to its start.
 *
 * Home and Prayer are long lists that grow as you scroll. Coming back to one —
 * from the header, the phone bar, the logo or the left menu — should show what
 * is new from the top, the way every social app behaves, not the page you left
 * forty posts down with yesterday's posts still loaded.
 *
 * RESET, NOT INVALIDATE. Invalidating an infinite list refetches every page
 * already loaded, so somebody ten pages deep waits for ten requests and still
 * lands mid-list. Resetting drops them and fetches the first page alone.
 */
import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

/** The lists a tab opens onto, by path. */
const LISTS: Record<string, readonly string[][]> = {
  '/': [['feed', 'home']],
  '/prayer': [['prayer']],
  '/testimonies': [['feed', 'home', 'testimony']],
}

export function useTabTap() {
  const qc = useQueryClient()
  const { pathname } = useLocation()

  return (to: string) => {
    for (const key of LISTS[to] ?? []) {
      void qc.resetQueries({ queryKey: key })
    }
    // Already here: nothing navigates, so scroll ourselves. Going elsewhere,
    // ScrollToTop below takes the new page to its top.
    if (pathname === to) window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

/** Every new page opens at its top. Back and forward are left to the browser,
 *  so returning to a page lands where you were. */
export function ScrollToTop() {
  const { pathname } = useLocation()
  const type = useNavigationType()
  useEffect(() => {
    if (type !== 'POP') window.scrollTo(0, 0)
  }, [pathname, type])
  return null
}
